import React, { useState, useEffect } from 'react';
import { Button, Input, Table, Tag, Spin, Toast, Space } from '@douyinfe/semi-ui';
import { IconSearch, IconRefresh } from '@douyinfe/semi-icons';
import { API, timestamp2string } from '../helpers';
import { stringToColor } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';
import { renderModelPrice, renderQuota } from '../helpers/render';
import { Tooltip, Modal } from '@douyinfe/semi-ui';

function renderTimestamp(timestamp) {
  return timestamp2string(timestamp);
}

function renderIsStream(bool) {
  return bool ? (
    <Tag color="blue" size="small">
      流
    </Tag>
  ) : (
    <Tag color="purple" size="small">
      非流
    </Tag>
  );
}

function renderUseTime(type) {
  const time = parseInt(type, 10);
  if (time < 101) return <Tag color="green" size="small">{time}s</Tag>;
  if (time < 300) return <Tag color="orange" size="small">{time}s</Tag>;
  return <Tag color="red" size="small">{time}s</Tag>;
}

function parseOther(record) {
  try {
    const raw = record?.other;
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    if (raw === '') return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function getCacheTokens(record) {
  const other = parseOther(record);
  const n = Number(other.cache_tokens || 0);
  return Number.isFinite(n) ? n : 0;
}

function formatInt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('zh-CN');
}

const emptyState = {
  totalGranted: 0,
  totalUsed: 0,
  totalAvailable: 0,
  unlimitedQuota: false,
  expiresAt: 0,
  tokenName: '',
  logs: [],
  tokenValid: false,
};

const TOKEN_STORAGE_KEY = 'whale-saved-token';

const readSavedToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const writeSavedToken = (value) => {
  try {
    const key = (value || '').trim();
    if (key) localStorage.setItem(TOKEN_STORAGE_KEY, key);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const LogsTable = ({ layout = 'b-side' }) => {
  const [apikey, setAPIKey] = useState(() => readSavedToken());
  const [activeTabKey, setActiveTabKey] = useState('');
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [baseUrl, setBaseUrl] = useState('');
  const [queried, setQueried] = useState(false);
  const baseUrls = React.useMemo(
    () => JSON.parse(process.env.REACT_APP_BASE_URL),
    []
  );

  useEffect(() => {
    const firstKey = Object.keys(baseUrls)[0];
    setActiveTabKey(firstKey);
    setBaseUrl(baseUrls[firstKey]);
  }, [baseUrls]);

  const handleAPIKeyChange = (value) => {
    setAPIKey(value);
    const key = (value || '').trim();
    if (!key) {
      writeSavedToken('');
      return;
    }
    if (/^sk-[a-zA-Z0-9]{48}$/.test(key)) {
      writeSavedToken(key);
    }
  };

  const resetData = (key) => {
    setTabData((prevData) => ({
      ...prevData,
      [key]: { ...emptyState },
    }));
  };

  const fetchData = async () => {
    const key = (apikey || '').trim();
    if (!key) {
      Toast.warning('请先输入令牌，再进行查询');
      return;
    }
    if (!/^sk-[a-zA-Z0-9]{48}$/.test(key)) {
      Toast.error('令牌格式非法！');
      return;
    }
    writeSavedToken(key);
    setLoading(true);
    setQueried(true);
    setCurrentPage(1);
    let newTabData = { ...emptyState };

    try {
      if (process.env.REACT_APP_SHOW_BALANCE === 'true') {
        const usageRes = await API.get(`${baseUrl}/api/usage/token/`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        const usageData = usageRes.data;
        if (usageData.code) {
          const d = usageData.data;
          newTabData.unlimitedQuota = d.unlimited_quota;
          newTabData.totalGranted = d.total_granted;
          newTabData.totalUsed = d.total_used;
          newTabData.totalAvailable = d.total_available;
          newTabData.expiresAt = d.expires_at;
          newTabData.tokenName = d.name;
          newTabData.tokenValid = true;
        } else {
          Toast.error(usageData.message || '查询令牌信息失败');
        }
      }
    } catch (e) {
      console.log(e);
      const status = e?.response?.status;
      if (status === 429) {
        Toast.error('请求过于频繁，请稍后再试');
      } else {
        Toast.error('查询令牌信息失败，请检查令牌是否正确');
      }
      resetData(activeTabKey);
      setLoading(false);
      return;
    }

    try {
      if (process.env.REACT_APP_SHOW_DETAIL === 'true') {
        const logRes = await API.get(`${baseUrl}/api/log/token`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        const { success, data: logData } = logRes.data;
        if (success) {
          newTabData.logs = Array.isArray(logData) ? logData.slice() : [];
        } else {
          Toast.error('查询调用详情失败，请输入正确的令牌');
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        Toast.error('明细请求过于频繁，额度已显示，明细请稍后重试');
      } else {
        Toast.error('查询调用详情失败，请输入正确的令牌');
      }
    }

    setTabData((prevData) => ({
      ...prevData,
      [activeTabKey]: newTabData,
    }));
    setLoading(false);
  };

  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        Toast.success('已复制');
        return;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        textArea.remove();
        Toast.success('已复制');
      } catch (err) {
        textArea.remove();
        Modal.error({ title: '无法复制，请手动复制', content: text });
      }
    } catch (err) {
      Modal.error({ title: '无法复制，请手动复制', content: text });
    }
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      render: renderTimestamp,
      sorter: (a, b) => a.created_at - b.created_at,
      defaultSortOrder: 'descend',
      width: 150,
    },
    {
      title: '模型',
      dataIndex: 'model_name',
      render: (text, record) =>
        record.type === 0 || record.type === 2 ? (
          <Tag color={stringToColor(text)} size="small" onClick={() => copyText(text)}>
            {text}
          </Tag>
        ) : null,
      sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
      width: 110,
    },
    {
      title: '用时',
      dataIndex: 'use_time',
      render: (text, record) =>
        record.model_name?.startsWith('mj_') ? null : (
          <Space>
            {renderUseTime(text)}
            {renderIsStream(record.is_stream)}
          </Space>
        ),
      sorter: (a, b) => a.use_time - b.use_time,
      width: 100,
    },
    {
      title: '提示',
      dataIndex: 'prompt_tokens',
      render: (text, record) =>
        record.model_name?.startsWith('mj_')
          ? null
          : record.type === 0 || record.type === 2
            ? formatInt(text)
            : null,
      sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
      width: 88,
    },
    {
      title: '缓存',
      dataIndex: 'cache_tokens',
      render: (_, record) => {
        if (record.type !== 0 && record.type !== 2) return null;
        const cache = getCacheTokens(record);
        if (cache <= 0) {
          return <Tag color="grey" size="small">未命中</Tag>;
        }
        return (
          <Tooltip content={`缓存 ${formatInt(cache)} / 提示 ${formatInt(record.prompt_tokens)}`}>
            <span className="w-cache-cell">
              <b>{formatInt(cache)}</b>
            </span>
          </Tooltip>
        );
      },
      sorter: (a, b) => getCacheTokens(a) - getCacheTokens(b),
      width: 120,
    },
    {
      title: '补全',
      dataIndex: 'completion_tokens',
      render: (text, record) =>
        parseInt(text, 10) > 0 && (record.type === 0 || record.type === 2) ? formatInt(text) : null,
      sorter: (a, b) => a.completion_tokens - b.completion_tokens,
      width: 72,
    },
    {
      title: '花费',
      dataIndex: 'quota',
      render: (text, record) => {
        if (record.type !== 0 && record.type !== 2) return null;
        const other = parseOther(record);
        const priceText = renderModelPrice(
          record.prompt_tokens,
          record.completion_tokens,
          other.model_ratio,
          other.model_price,
          other.completion_ratio,
          other.group_ratio
        );
        return (
          <Tooltip content={priceText || '花费'}>
            <span>{renderQuota(text, 6)}</span>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.quota - b.quota,
      width: 96,
    },
  ];

  const activeTabData = tabData[activeTabKey] || emptyState;
  const pageStart = (currentPage - 1) * pageSize;
  const pagedLogs = activeTabData.logs.slice(pageStart, pageStart + pageSize);

  const searchBar = (
    <div className="w-search-bar">
      <div className="w-search-row">
        <Input
          showClear
          size="large"
          value={apikey}
          onChange={handleAPIKeyChange}
          placeholder="粘贴 sk- 令牌后点查询"
          prefix={<IconSearch />}
          className="w-search-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') fetchData();
          }}
        />
        <Button
          type="primary"
          theme="solid"
          size="large"
          className="w-search-btn"
          onClick={fetchData}
          loading={loading}
          icon={queried ? <IconRefresh /> : <IconSearch />}
        >
          {loading ? '查询中' : queried ? '重新查询' : '查询'}
        </Button>
      </div>
      {Object.keys(baseUrls).length > 1 && (
        <div className="w-site-tabs">
          {Object.entries(baseUrls).map(([key, url]) => (
            <button
              key={key}
              type="button"
              className={activeTabKey === key ? 'active' : ''}
              onClick={() => {
                setActiveTabKey(key);
                setBaseUrl(url);
              }}
            >
              {key}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const showBalance =
    process.env.REACT_APP_SHOW_BALANCE === 'true' && activeTabData.tokenValid;
  const showLogs =
    process.env.REACT_APP_SHOW_DETAIL === 'true' && activeTabData.tokenValid;

  const resultPanel =
    showBalance || showLogs ? (
      <section className="w-result-section w-result-merged">
        {showBalance ? (
          <div className="w-stat-grid w-stat-grid-four">
            <div className="w-stat-item">
              <span>令牌名称</span>
              <b>{activeTabData.tokenName || '未知令牌'}</b>
            </div>
            <div className="w-stat-item">
              <span>总额度</span>
              <b>
                {activeTabData.unlimitedQuota
                  ? '无限'
                  : renderQuota(activeTabData.totalGranted, 3)}
              </b>
            </div>
            <div className="w-stat-item">
              <span>剩余额度</span>
              <b>
                {activeTabData.unlimitedQuota
                  ? '无限制'
                  : renderQuota(activeTabData.totalAvailable, 3)}
              </b>
            </div>
            <div className="w-stat-item">
              <span>已用</span>
              <b>
                {activeTabData.unlimitedQuota ? '—' : renderQuota(activeTabData.totalUsed, 3)}
              </b>
            </div>
          </div>
        ) : null}

        {showLogs ? (
          <div className="w-logs-block">
            <Spin spinning={loading}>
              {activeTabData.logs.length === 0 && !loading ? (
                <div className="w-empty-inline">
                  {queried ? '暂无调用记录' : '查询后将在此展示调用明细'}
                </div>
              ) : (
                <>
                  <div className="w-log-cards w-log-cards-mobile">
                    {pagedLogs.map((log, idx) => {
                        const cache = getCacheTokens(log);
                        return (
                          <article key={log.id || idx} className="w-log-card">
                            <div className="w-log-card-top">
                              <Tag color={stringToColor(log.model_name || '')} size="small">
                                {log.model_name || '-'}
                              </Tag>
                              <span className="w-log-time">{renderTimestamp(log.created_at)}</span>
                            </div>
                            <div className="w-log-card-grid">
                              <div>
                                <span>用时</span>
                                <b>
                                  {log.use_time}s {log.is_stream ? '流' : '非流'}
                                </b>
                              </div>
                              <div>
                                <span>提示</span>
                                <b>{formatInt(log.prompt_tokens)}</b>
                              </div>
                              <div>
                                <span>缓存</span>
                                <b className={cache > 0 ? 'is-cache-hit' : 'is-cache-miss'}>
                                  {cache > 0 ? formatInt(cache) : '未命中'}
                                </b>
                              </div>
                              <div>
                                <span>补全</span>
                                <b>{formatInt(log.completion_tokens)}</b>
                              </div>
                              <div>
                                <span>花费</span>
                                <b>{renderQuota(log.quota, 6)}</b>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    {activeTabData.logs.length > pageSize && (
                      <div className="w-mobile-pager">
                        <Button
                          disabled={currentPage <= 1}
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        >
                          上一页
                        </Button>
                        <span>
                          {currentPage} / {Math.ceil(activeTabData.logs.length / pageSize)}
                        </span>
                        <Button
                          disabled={
                            currentPage >= Math.ceil(activeTabData.logs.length / pageSize)
                          }
                          onClick={() =>
                            setCurrentPage((p) =>
                              Math.min(
                                Math.ceil(activeTabData.logs.length / pageSize),
                                p + 1
                              )
                            )
                          }
                        >
                          下一页
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="w-log-table-wrap w-log-table-desktop">
                    <Table
                      columns={columns}
                      dataSource={pagedLogs}
                      pagination={{
                        pageSize,
                        currentPage,
                        hideOnSinglePage: true,
                        showSizeChanger: true,
                        pageSizeOpts: [10, 20, 50, 100],
                        onPageSizeChange: (s) => {
                          setPageSize(s);
                          setCurrentPage(1);
                        },
                        onPageChange: (p) => setCurrentPage(p),
                        showTotal: true,
                        showQuickJumper: true,
                        total: activeTabData.logs.length,
                        style: { marginTop: 12 },
                      }}
                      scroll={{ x: 'max-content' }}
                      size="small"
                    />
                  </div>
                </>
              )}
            </Spin>
          </div>
        ) : null}
      </section>
    ) : null;

  return (
    <div className={`w-logs-root layout-${layout}`}>
      {searchBar}
      {resultPanel ? (
        <Spin spinning={loading && !activeTabData.tokenValid}>{resultPanel}</Spin>
      ) : null}
    </div>
  );
};

export default LogsTable;
